import classNames from "classnames"
import ApplicationComponent from "legacy/ApplicationComponent"
import ContractLeftTab from "legacy/contract-groups-v2/ContractLeftTab"
import { humanize, isPresent } from "legacy/helpers.es6"
import PropTypes from "prop-types"
import React from "react"

// Note: $context is loaded globally

// Missing pluralize function, copied from ReservationLabelsController.jsx
function pluralize(word, count, includeCount = false) {
  const pluralizedWord = count === 1 ? word : `${word}s`
  return includeCount ? `${count} ${pluralizedWord}` : pluralizedWord
}

class ContractLeftTabLayout extends ApplicationComponent {
  feesLength() {
    return this.props.form.feesAndDiscounts.filter(
      (fee) => fee.category === "fee" || fee.category === "electric"
    ).length
  }

  discountsLength() {
    return this.props.form.feesAndDiscounts.filter(
      (fee) => fee.category === "discount"
    ).length
  }

  renderFeesAndDiscountCount() {
    return [
      <div key="0">
        {this.feesLength() > 0 && (
          <span>
            {this.feesLength()} {pluralize("fee", this.feesLength())}
          </span>
        )}
      </div>,
      <div key="1">
        {this.discountsLength() > 0 && (
          <span>
            {this.discountsLength()}{" "}
            {pluralize("discount", this.discountsLength())}
          </span>
        )}
      </div>,
    ]
  }

  renderPaymentMethodList() {
    const acceptedPaymentMethodText =
      this.props.form.acceptedPaymentMethods.map((paymentMethod) => {
        switch (paymentMethod) {
          case "card":
            return "Card"
          case "us_bank_account":
            return "Bank Account"
          default:
            return "Other"
        }
      })

    if (this.props.form.allowDirectPayment) {
      acceptedPaymentMethodText.push("Offline Payment")
    }

    return acceptedPaymentMethodText
  }

  changeTab(tab) {
    if (this.props.changeTab) {
      this.props.changeTab(tab)
    }
  }

  isActiveTab(tab) {
    if (this.isSelectable()) {
      return this.props.activeTab === tab
    } else {
      return false
    }
  }

  isSelectable() {
    return this.props.activeTab && !!this.props.tabs
  }

  renderHeaderSection() {
    if (this.props.isGroup) {
      return (
        <div className="p-6">
          <h3 className="my-1 font-semibold">
            {this.props.form.name ? this.props.form.name : "New Contract"}
          </h3>
          {this.props.children}
        </div>
      )
    } else {
      return (
        <div
          onClick={() => this.changeTab("contactAndBoat")}
          className={classNames({ "cursor-pointer": this.isSelectable() })}
        >
          <div
            className={classNames(
              "flex flex-col justify-between rounded-t p-6",
              {
                "relative z-10 -m-px border border-yellow-400 bg-yellow-100":
                  this.isActiveTab("contactAndBoat"),
              }
            )}
          >
            <div className="text-base font-semibold">
              {this.props.form.name}
            </div>
            <div className="flex flex-col space-y-2">
              <div className="mt-4 flex justify-between">
                <span>{this.props.form.tabContactName || "Customer"}</span>
                <span>Total</span>
              </div>
              <div className="flex justify-between">
                <em>{this.props.form.tabBoatName || "Boat"}</em>
                <span className="font-semibold">
                  {this.props.form.tabTotalPrice || "-"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )
    }
  }

  pricingStructureText() {
    if (this.props.form.rate.pricingStructure === "per_season") {
      return "Per Full Stay"
    } else if (this.props.form.rate.pricingStructure === "by_foot_per_season") {
      return "By Foot Per Full Stay"
    } else if (this.props.form.rate.pricingStructure === "sqft_per_season") {
      return "Sqft Per Full Stay"
    } else {
      return humanize(this.props.form.rate.pricingStructure)
    }
  }

  getTab(tab) {
    switch (tab) {
      case "itinerary":
        return (
          <ContractLeftTab
            title="Itinerary"
            isSelectable={this.isSelectable()}
            isActive={this.isActiveTab("itinerary")}
            onClick={() => this.changeTab("itinerary")}
          >
            {(this.props.form.spaceType ||
              this.props.form.contractType ||
              this.props.form.startDate) && (
              <div className="flex flex-col items-end space-y-3">
                <div className="flex space-x-2 sm:flex-col sm:items-end sm:space-y-2 lg:flex-row lg:space-y-0">
                  {this.props.form.spaceType ? (
                    <div className="badge badge-blue">
                      {humanize(this.props.form.spaceType)}
                    </div>
                  ) : null}
                  {this.props.form.contractType ? (
                    <div className="badge badge-blue">
                      {humanize(this.props.form.contractType)}
                    </div>
                  ) : null}
                </div>
                {this.props.form.startDate ? (
                  <div>
                    <span className="text-muted">from</span>{" "}
                    {this.props.form.startDate}
                    {this.props.form.contractType !== "month_to_month" && (
                      <span>
                        <span className="text-muted"> to</span>{" "}
                        {this.props.form.endDate}
                      </span>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </ContractLeftTab>
        )
      case "rate":
        return (
          <ContractLeftTab
            title="Rate"
            isSelectable={this.isSelectable()}
            isActive={this.isActiveTab("rate")}
            onClick={() => this.changeTab("rate")}
          >
            {this.props.form.rate && this.props.form.rate.pricingStructure && (
              <div className="flex flex-col items-end space-y-3">
                <div>
                  ${this.props.form.rate.amount}{" "}
                  <span className="text-muted">
                    {this.pricingStructureText()}
                  </span>
                </div>
                {isPresent(this.props.form.namedRateId) && (
                  <div>
                    <span className="badge badge-blue">
                      {
                        this.props.namedRates.find(
                          (rate) => rate.id === this.props.form.namedRateId
                        ).name
                      }
                    </span>
                  </div>
                )}
              </div>
            )}
          </ContractLeftTab>
        )
      case "billing":
        return (
          <ContractLeftTab
            title="Billing"
            isSelectable={this.isSelectable()}
            isActive={this.isActiveTab("billing")}
            onClick={() => this.changeTab("billing")}
          >
            {this.props.form.billingCycle && (
              <div className="flex flex-col items-end space-y-3">
                <div>
                  <div className="badge badge-blue">
                    {humanize(this.props.form.billingCycle)}
                  </div>
                </div>
              </div>
            )}
            {!this.props.form.billingCycle && <span>Not Set</span>}
          </ContractLeftTab>
        )
      case "paymentMethods":
        return (
          <ContractLeftTab
            title="Payment Methods"
            isSelectable={this.isSelectable()}
            isActive={this.isActiveTab("paymentMethods")}
            onClick={() => this.changeTab("paymentMethods")}
          >
            <div>
              {this.renderPaymentMethodList().length > 0
                ? this.renderPaymentMethodList().join(", ")
                : "Not Set"}
            </div>
          </ContractLeftTab>
        )
      case "installments":
        return (
          <ContractLeftTab
            title="Installments"
            isSelectable={this.isSelectable()}
            isActive={this.isActiveTab("installments")}
            onClick={() => this.changeTab("installments")}
          >
            {this.props.form.rate.contractInstallments.length && (
              <div className="flex flex-col items-end space-y-3">
                <div>
                  <div className="badge badge-blue">
                    {this.props.form.rate.contractInstallments.length}{" "}
                    {pluralize(
                      "installment",
                      this.props.form.rate.contractInstallments.length
                    )}
                  </div>
                </div>
              </div>
            )}
          </ContractLeftTab>
        )
      case "deposit":
        return (
          <ContractLeftTab
            title="Security Deposit"
            isSelectable={this.isSelectable()}
            isActive={this.isActiveTab("deposit")}
            onClick={() => this.changeTab("deposit")}
          >
            {this.props.form.depositAmount && (
              <span>
                ${this.props.form.depositAmount}{" "}
                <span className="text-muted">security deposit</span>
              </span>
            )}
          </ContractLeftTab>
        )
      case "charges":
        return (
          <ContractLeftTab
            title="Charges"
            isSelectable={this.isSelectable()}
            isActive={this.isActiveTab("charges")}
            onClick={() => this.changeTab("charges")}
          >
            {this.props.form.posItemsData &&
              this.props.form.posItemsData.length > 0 && (
                <span>
                  {this.props.form.posItemsData.length}{" "}
                  {pluralize("Item", this.props.form.posItemsData.length)}
                </span>
              )}
          </ContractLeftTab>
        )
      case "items":
        return (
          <ContractLeftTab
            title="Items"
            isSelectable={this.isSelectable()}
            isActive={this.isActiveTab("items")} // isActive will never be on in this view
            onClick={() => this.changeTab("items")} // will never get used in this view, only included for legacy compatibility reasons
          >
            {this.props.form.posItemsData &&
              this.props.form.posItemsData.length > 0 && (
                <div className="badge badge-blue">
                  {this.props.form.posItemsData.length}{" "}
                  {pluralize("Item", this.props.form.posItemsData.length)}
                </div>
              )}
          </ContractLeftTab>
        )
      case "fees":
        return (
          <ContractLeftTab
            title="Fees & Discounts"
            isSelectable={this.isSelectable()}
            isActive={this.isActiveTab("fees")}
            onClick={() => this.changeTab("fees")}
          >
            {this.props.form.feesAndDiscounts &&
              this.renderFeesAndDiscountCount()}
          </ContractLeftTab>
        )
      case "documents":
        return (
          <ContractLeftTab
            title="Documents"
            isSelectable={this.isSelectable()}
            isActive={this.isActiveTab("documents")}
            onClick={() => this.changeTab("documents")}
          >
            {this.props.form.attachments &&
              this.props.form.attachments.length > 0 && (
                <span>
                  {this.props.form.attachments.length}{" "}
                  {pluralize("document", this.props.form.attachments.length)}
                </span>
              )}
          </ContractLeftTab>
        )
      default:
        return null
    }
  }

  render() {
    return (
      <div
        className="card col-span-12 self-start p-0 md:sticky md:col-span-5"
        style={{ top: 16 }}
      >
        {this.renderHeaderSection()}

        {/* ---- Tabs ---- */}

        {this.getTab("itinerary")}

        {this.getTab("rate")}

        {!$context.mbmEnabled ? this.getTab("billing") : null}

        {this.getTab("paymentMethods")}

        {$context.mbmEnabled && this.props.form.billingCycle === "installments"
          ? this.getTab("installments")
          : null}

        {!$context.mbmEnabled ? this.getTab("deposit") : null}

        {!$context.mbmEnabled ? this.getTab("charges") : null}

        {$context.mbmEnabled ? this.getTab("items") : null}

        {!$context.mbmEnabled ? this.getTab("fees") : null}

        {this.getTab("documents")}
      </div>
    )
  }
}

ContractLeftTabLayout.propTypes = {
  activeTab: PropTypes.string,
  changeTab: PropTypes.func,
  children: PropTypes.node,
  form: PropTypes.shape({
    name: PropTypes.string,
    spaceType: PropTypes.string,
    contractType: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    tabContactName: PropTypes.string,
    tabBoatName: PropTypes.string,
    tabTotalPrice: PropTypes.string,
    acceptedPaymentMethods: PropTypes.array.isRequired,
    allowDirectPayment: PropTypes.bool,
    rate: PropTypes.shape({
      pricingStructure: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      contractInstallments: PropTypes.array,
    }),
    namedRateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    billingCycle: PropTypes.string,
    depositAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    posItemsData: PropTypes.array,
    feesAndDiscounts: PropTypes.array,
    attachments: PropTypes.array,
  }).isRequired,
  isGroup: PropTypes.bool,
  namedRates: PropTypes.array,
  tabs: PropTypes.array,
}

export default ContractLeftTabLayout
